/**
 * Workflow Graph Traversal Utilities
 * 
 * Provides functions to traverse ReactFlow workflow graphs and extract
 * information about previous nodes and their outputs for parameter configuration.
 */

import { Node, Edge } from '@xyflow/react';
import actionTypesConfigRaw from '../config/automaticActionTypes.json';
import {
  ActionTypeDefinition,
  ActionTypeOutputField,
  PreviousNodeOutput,
  ModuleDataField,
  ActionTypesConfig,
} from '../types/automaticActions';

// Type the imported JSON
const actionTypesConfig = actionTypesConfigRaw as ActionTypesConfig;

/**
 * Workflow node data interface
 */
interface WorkflowNodeData {
  label?: string;
  name?: string;
  actionTypeId?: string;
  stateId?: number;
  isAutomatic?: boolean;
  isManual?: boolean;
  automaticAction?: {
    stateType?: string | number;
    config?: any;
  };
  extraAttributes?: {
    automatic?: boolean;
    [key: string]: any;
  };
  [key: string]: any;
}

/**
 * Get all nodes that come before a specific node in the workflow graph
 * Uses breadth-first search to traverse backwards through edges
 * 
 * @param targetNodeId - The node ID to find predecessors for
 * @param nodes - All nodes in the workflow
 * @param edges - All edges in the workflow
 * @returns Array of nodes that come before the target node
 */
export function getPreviousNodes(
  targetNodeId: string,
  nodes: Node[],
  edges: Edge[]
): Node[] {
  const previousNodes: Node[] = [];
  const visited = new Set<string>();
  const queue: string[] = [targetNodeId];

  visited.add(targetNodeId);

  while (queue.length > 0) {
    const currentNodeId = queue.shift()!;

    // Find all edges that point TO the current node
    const incomingEdges = edges.filter(edge => edge.target === currentNodeId);

    for (const edge of incomingEdges) {
      const sourceNodeId = edge.source;
      
      if (!visited.has(sourceNodeId)) {
        visited.add(sourceNodeId);
        queue.push(sourceNodeId);
        
        // Find the actual node object
        const sourceNode = nodes.find(n => n.id === sourceNodeId);
        if (sourceNode) {
          previousNodes.push(sourceNode);
        }
      }
    }
  }

  return previousNodes;
}

/**
 * Get action type definition by ID
 */
export function getActionTypeDefinition(actionTypeId: string): ActionTypeDefinition | null {
  return actionTypesConfig.actionTypes[actionTypeId] || null;
}

/**
 * Get action type definition by idStateType
 */
export function getActionTypeByStateTypeId(idStateType: number): ActionTypeDefinition | null {
  const actionType = Object.values(actionTypesConfig.actionTypes).find(
    at => at.idStateType === idStateType
  );
  return actionType || null;
}

/**
 * Get output fields for an automatic action node
 */
function getAutomaticNodeOutputs(node: Node): ActionTypeOutputField[] {
  const nodeData = node.data as WorkflowNodeData;
  
  // Check if this is an automatic action node
  if (!nodeData?.automaticAction) {
    return [];
  }

  // Get the action type from automaticAction.stateType
  const stateType = nodeData.automaticAction.stateType;
  if (!stateType) {
    return [];
  }

  // Try to find action type by ID (string) or by idStateType (number)
  let actionType: ActionTypeDefinition | null = null;
  
  if (typeof stateType === 'string') {
    actionType = getActionTypeDefinition(stateType);
  } else if (typeof stateType === 'number') {
    actionType = getActionTypeByStateTypeId(stateType);
  }

  return actionType?.outputFields || [];
}

/**
 * Get module data fields for a manual or start node
 * This should be customized based on the actual module/entity structure
 */
function getModuleDataOutputs(moduleId: number): ModuleDataField[] {
  // TODO: This should be fetched from the backend based on the module
  // For now, returning common fields as an example
  
  // Common fields that appear in most entities
  const commonFields: ModuleDataField[] = [
    { name: 'id', type: 'number', description: 'Entity ID', label: 'ID' },
    { name: 'createdAt', type: 'datetime', description: 'Creation timestamp', label: 'Created At' },
    { name: 'updatedAt', type: 'datetime', description: 'Last update timestamp', label: 'Updated At' },
    { name: 'createdBy', type: 'number', description: 'User ID who created the entity', label: 'Created By' },
  ];

  // Module-specific fields
  // TODO: Replace with actual API call to get entity schema
  const moduleSpecificFields: Record<number, ModuleDataField[]> = {
    1: [ // Time Off module example
      { name: 'employeeId', type: 'number', description: 'Employee ID', label: 'Employee ID' },
      { name: 'startDate', type: 'datetime', description: 'Time off start date', label: 'Start Date' },
      { name: 'endDate', type: 'datetime', description: 'Time off end date', label: 'End Date' },
      { name: 'reason', type: 'string', description: 'Time off reason', label: 'Reason' },
      { name: 'status', type: 'string', description: 'Request status', label: 'Status' },
    ],
    2: [ // Invoice module example
      { name: 'invoiceNumber', type: 'string', description: 'Invoice number', label: 'Invoice Number' },
      { name: 'amount', type: 'number', description: 'Invoice amount', label: 'Amount' },
      { name: 'clientId', type: 'number', description: 'Client ID', label: 'Client ID' },
      { name: 'dueDate', type: 'datetime', description: 'Payment due date', label: 'Due Date' },
    ],
  };

  return [...commonFields, ...(moduleSpecificFields[moduleId] || [])];
}

/**
 * Get all automatic action outputs available to a specific node
 * This includes outputs from all previous automatic actions and module data
 * 
 * @param targetNodeId - The node ID to get available outputs for
 * @param nodes - All nodes in the workflow
 * @param edges - All edges in the workflow
 * @param moduleId - The module ID for getting entity fields
 * @returns Array of previous node outputs available for parameter configuration
 */
export function getAvailablePreviousOutputs(
  targetNodeId: string,
  nodes: Node[],
  edges: Edge[],
  moduleId?: number
): PreviousNodeOutput[] {
  const previousNodes = getPreviousNodes(targetNodeId, nodes, edges);
  const availableOutputs: PreviousNodeOutput[] = [];

  for (const node of previousNodes) {
    const nodeData = node.data as WorkflowNodeData;
    
    // Only include automatic action nodes - skip manual/start nodes
    if (nodeData?.automaticAction) {
      // Automatic action node - get outputs from action type definition
      const outputFields = getAutomaticNodeOutputs(node);
      
      if (outputFields.length > 0) {
        const stateType = nodeData.automaticAction.stateType;
        const actionTypeId = typeof stateType === 'string' ? stateType : undefined;
        
        availableOutputs.push({
          nodeId: node.id,
          nodeName: nodeData?.name || `Node ${node.id}`,
          nodeType: 'automatic',
          stateId: nodeData?.stateId || null,
          actionTypeId: actionTypeId,
          outputFields,
        });
      }
    }
    // Manual and start nodes are intentionally excluded
    // Only automatic action nodes produce structured outputs that can be used as parameters
  }

  return availableOutputs;
}

/**
 * Get module data fields for parameter configuration
 * This provides access to the current entity/form data
 */
export function getModuleDataFields(moduleId: number): ModuleDataField[] {
  return getModuleDataOutputs(moduleId);
}

/**
 * Validate if a parameter reference is valid
 * Checks if the referenced node and field actually exist
 */
export function validateParameterReference(
  nodeId: string,
  fieldName: string,
  availableOutputs: PreviousNodeOutput[]
): boolean {
  const node = availableOutputs.find(output => output.nodeId === nodeId);
  if (!node) return false;

  const field = node.outputFields.find(f => f.name === fieldName);
  return !!field;
}

/**
 * Get the full path description for a parameter reference
 * Example: "Send Welcome Email → messageId"
 */
export function getParameterReferencePath(
  nodeId: string,
  fieldName: string,
  availableOutputs: PreviousNodeOutput[]
): string {
  const node = availableOutputs.find(output => output.nodeId === nodeId);
  if (!node) return 'Unknown';

  const field = node.outputFields.find(f => f.name === fieldName);
  const fieldDescription = field ? `${fieldName} (${field.type})` : fieldName;

  return `${node.nodeName} → ${fieldDescription}`;
}

/**
 * Sort previous outputs by workflow execution order
 * Nodes closer to the start appear first
 */
export function sortPreviousOutputsByOrder(
  outputs: PreviousNodeOutput[],
  nodes: Node[],
  edges: Edge[]
): PreviousNodeOutput[] {
  // TODO: Implement topological sort based on edges
  // For now, just return as-is
  return outputs;
}
